/**
 * The client half of the photo pipeline: upload, queue, poll.
 *
 * Shared because both clients must do the same three steps in the same order,
 * and because the polling cadence is a product decision rather than a
 * per-platform detail — see `backend/app/services/scan.py` for what happens on
 * the other end.
 *
 * The response shapes are the *generated* ones — see `packages/api-client`.
 * They are aliased rather than restated so that renaming a Pydantic field
 * fails the typecheck here instead of failing at runtime in front of a user.
 */

import type { components } from "@calorio/api-client";

type Schemas = components["schemas"];

export type ScanStatus = Schemas["ScanStatus"];
export type ScanMethod = Schemas["ScanMethod"];

/**
 * What a failed read did manage, when it managed some of it.
 *
 * A panel yielding three of four figures is refused rather than completed with
 * a fabricated zero — but throwing away the three that were legible makes the
 * user retype a label that was mostly read. Offer these as a starting point.
 *
 * Narrower than the schema, deliberately. FastAPI types the field as an open
 * `dict[str, float]` because Pydantic has no reason to enumerate the keys, but
 * the parser emits exactly these and a client switching on a key it never
 * produces is a branch that can only be dead.
 *
 * The list is the server's own whitelist in `Scan.partial`, and the two move
 * together. `salt` and `sodium` are both here because a panel prints one or
 * the other; `saturates` because the reader now keeps that figure rather than
 * discarding it.
 */
export type PanelPartial = Partial<
  Record<
    "energy" | "protein" | "carbs" | "fat" | "saturates" | "fiber" | "sugar" | "salt" | "sodium",
    number
  >
>;

export type Scan = Omit<Schemas["ScanOut"], "partial"> & { partial?: PanelPartial | null };

/**
 * Which of the two refusals a failed product scan was.
 *
 * They want opposite answers on screen — a panel we could see but not read
 * wants a better photograph, while a plate with no packaging will never read
 * and wants the manual form — so the client has to be able to tell them apart
 * without matching on the wording of `error`.
 */
export type ScanFailure = Schemas["ScanFailure"];

export type FoodItem = Schemas["FoodItemOut"];

/**
 * The subset of `@calorio/api-client` this package depends on.
 *
 * Declared structurally rather than imported so `@calorio/core` stays usable
 * with any client that satisfies it — a test double, for instance, doesn't need
 * to bring the auth machinery along.
 */
export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  patch<T>(path: string, body: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
}

export class ScanFailed extends Error {
  /**
   * The scan that failed, when there was one.
   *
   * A refusal still carries what was legible — see `Scan.partial` — and throwing
   * a bare message discards it, which is how a user ends up retyping a label the
   * reader got three quarters of.
   */
  constructor(
    message: string,
    readonly scan?: Scan,
  ) {
    super(message);
  }
}

/**
 * Barcode → food item, with no photograph in it at all.
 *
 * This is the fast path standing on its own, and separating it is what lets a
 * viewfinder behave like a barcode scanner. `scanPhoto` resolves a barcode too
 * — it is the first rung of the pipeline — but reaching that rung costs a
 * capture, an upload of the whole frame to object storage, a scan row and a
 * poll loop, all to answer a question the server settles with one indexed
 * lookup. That expense is why the camera could not simply *watch*: every decode
 * was a commitment, so the client had to freeze the frame and spend the upload
 * before it knew whether the code meant anything.
 *
 * Detached, a decode is cheap enough to be speculative. The feed stays live,
 * every frame is a free attempt, and the user aims until it reads.
 *
 * A miss is an ordinary outcome of pointing a camera at a shelf rather than an
 * error, so it resolves rather than throwing — and it carries the server's own
 * sentence, which already tells the user what to do about it ("we don't know
 * that barcode — photograph the nutrition label instead"). Restating that on
 * the client would put the same advice in two places and let them drift.
 * Everything else still throws: being offline or signed out is not a fact about
 * this barcode.
 */
export type BarcodeLookup =
  | { found: true; food: FoodItem }
  | { found: false; message: string };

/** What to say when the server missed but said nothing useful about why. */
const BARCODE_MISS_FALLBACK =
  "we don't know that barcode — photograph the nutrition label instead";

export async function lookupBarcode(api: ApiClient, barcode: string): Promise<BarcodeLookup> {
  try {
    const food = await api.get<FoodItem>(`/v1/foods/barcode/${encodeURIComponent(barcode)}`);
    return { found: true, food };
  } catch (error) {
    // Duck-typed rather than `instanceof ApiError`, for the same reason
    // `ApiClient` is a structural interface: any client that satisfies the
    // shape should work here, and a test double shouldn't have to import the
    // real error class to be able to say "404".
    const miss = error as { status?: unknown; detail?: unknown; message?: unknown } | null;
    if (miss?.status === 404) {
      const said = typeof miss.detail === "string" ? miss.detail : miss.message;
      return {
        found: false,
        message: typeof said === "string" && said.trim() ? said : BARCODE_MISS_FALLBACK,
      };
    }
    throw error;
  }
}

/**
 * Photograph → food item.
 *
 * `barcode` is the important optional argument. It is no longer how a *known*
 * product is looked up — `lookupBarcode` does that without an upload — so by
 * the time a photo is being taken, the code has usually already missed. Send it
 * anyway: `process_scan` writes it onto whatever the label reader manages to
 * parse, which is what turns one person's slow OCR into everyone else's cache
 * hit. On iOS it also still carries the client's only decode, since there is
 * none — see the server-side decoder in `backend/app/services/barcode.py`.
 */
export async function scanPhoto(
  api: ApiClient,
  image: Blob,
  options: { barcode?: string; signal?: AbortSignal } = {},
): Promise<Scan> {
  const ticket = await api.post<{ image_key: string; upload_url: string }>(
    "/v1/scans/upload-url",
    { media_type: image.type },
  );

  // Straight to object storage — the bytes never touch the API process.
  const upload = await fetch(ticket.upload_url, {
    method: "PUT",
    body: image,
    headers: { "Content-Type": image.type },
    signal: options.signal,
  });
  if (!upload.ok) throw new ScanFailed(`upload failed (${upload.status})`);

  const scan = await api.post<Scan>("/v1/scans", {
    image_key: ticket.image_key,
    barcode: options.barcode ?? null,
  });

  return pollScan(api, scan.id, options.signal);
}

/**
 * Poll until terminal.
 *
 * Backs off from 400ms to 2s: a barcode cache hit completes on the first poll,
 * while label OCR takes a couple of seconds, and hammering the API for the whole
 * of that wastes the user's battery and mobile data for no gain.
 */
export async function pollScan(
  api: ApiClient,
  scanId: string,
  signal?: AbortSignal,
  { timeoutMs = 90_000 }: { timeoutMs?: number } = {},
): Promise<Scan> {
  const deadline = Date.now() + timeoutMs;
  let delay = 400;

  while (Date.now() < deadline) {
    if (signal?.aborted) throw new ScanFailed("cancelled");

    const scan = await api.get<Scan>(`/v1/scans/${scanId}`);
    if (scan.status === "complete") return scan;
    if (scan.status === "failed") throw new ScanFailed(scan.error ?? "the scan failed", scan);

    await new Promise((resolve) => setTimeout(resolve, delay));
    delay = Math.min(delay * 1.5, 2000);
  }

  throw new ScanFailed("that took too long — try again");
}

/**
 * Whether to show the result as a measurement or a transcription.
 *
 * A barcode match is a lookup of printed data; a label read is OCR that can
 * misread a digit, so the UI flags it as worth checking. `"estimated"` remains
 * only for historical rows from the removed meal-estimate rung.
 */
export function resultTrust(scan: Scan): "measured" | "transcribed" | "estimated" {
  switch (scan.method) {
    case "barcode_cache":
    case "barcode_off":
      return "measured";
    case "label_ocr":
      return "transcribed";
    default:
      return "estimated";
  }
}
